/**
 * SumUp transactions triage view — ``/settings/sumup/transactions``
 * (Owner-only).
 *
 * An admin-table-baseline table of every SumUp payment intent (newest
 * first): every column sortable + per-column filter + sticky header +
 * compact ``DD.MM HH:MM`` timestamps with the full timestamp on hover.
 *
 * Status semantics (mirrors ``SumupPaymentStatus`` plus the derived
 * ``stuck`` flag from the route):
 *   - ``paid`` + settled (settlement_id set) → COMPLETED (green)
 *   - ``paid`` + NOT settled (``stuck``)     → PAYMENT SECURED — SYNCING
 *                                              (amber; the recoverable
 *                                              window the poller retries)
 *   - ``failed``                             → FAILED (red)
 *   - ``awaiting_payment``                   → AWAITING PAYMENT (blue)
 *   - ``cancelled`` / ``expired``            → neutral
 *
 * Each row expands to show its per-channel legs (channel / status /
 * last_error_code) + the intent's ``last_error_code`` / ``last_error``.
 * Stuck rows carry the reassurance line "Card charged — waiting to
 * finalize" so an operator never panics that money was lost.
 *
 * Force-close (DELETE) discards a dead intent. It is DISABLED with an
 * explanatory tooltip for paid-but-unsettled (stuck) rows: that money was
 * collected and must be settled by the poller, never discarded — the
 * backend enforces the same with a 409.
 *
 * Sized for mixed touch + mouse: comfortable controls, no oversized chrome.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";

import {
  ApiError,
  type SumupLegRow,
  type SumupTransactionRow,
  api,
} from "../../api/client";
import { QuickFilterDropdown } from "../../components/QuickFilterDropdown";
import { formatCompactDateTime, formatFullIso } from "../../utils/dateFormat";

const { Title, Text, Paragraph } = Typography;

const formatCents = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );

/** A timestamp cell: compact value, full local timestamp on hover. */
const TimeCell = ({ value }: { value: string | null }) => (
  <Tooltip title={formatFullIso(value)}>
    <span>{formatCompactDateTime(value)}</span>
  </Tooltip>
);

/** Status filter options (raw ``SumupPaymentStatus`` values + the derived
 * ``stuck`` pseudo-status the operator actually cares about). */
const STATUS_FILTER_OPTIONS = [
  { value: "stuck", label: "Payment secured — syncing" },
  { value: "paid", label: "Paid" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];

interface StatusView {
  color: string;
  label: string;
}

/** Resolve the colored tag for a row, folding ``stuck`` over the raw
 * status so a paid-but-unsettled intent never reads as a plain "paid". */
function statusView(row: SumupTransactionRow): StatusView {
  if (row.stuck) return { color: "orange", label: "PAYMENT SECURED — SYNCING" };
  switch (row.status) {
    case "paid":
      // paid + settled → the happy terminal state.
      return { color: "green", label: "COMPLETED" };
    case "awaiting_payment":
      return { color: "blue", label: "AWAITING PAYMENT" };
    case "failed":
      return { color: "red", label: "FAILED" };
    case "cancelled":
      return { color: "default", label: "CANCELLED" };
    case "expired":
      return { color: "default", label: "EXPIRED" };
    default:
      return { color: "default", label: row.status.toUpperCase() };
  }
}

const LEG_STATUS_COLOR: Record<string, string> = {
  paid: "green",
  pending: "blue",
  failed: "red",
  cancelled: "default",
  expired: "default",
  refunded: "purple",
};

/** Whether a row matches the active status filter (``stuck`` is derived,
 * everything else compares the raw status). */
function matchesStatusFilter(
  row: SumupTransactionRow,
  filter: string | null,
): boolean {
  if (!filter) return true;
  if (filter === "stuck") return row.stuck;
  return row.status === filter;
}

export const SumupTransactionsPage = () => {
  const [rows, setRows] = useState<SumupTransactionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<number | null>(null);

  // ?status=<filter> deep-link from the stuck-payments badge lands here
  // pre-filtered to the syncing window. The column-filter chrome reflects
  // the same value so the two stay in sync.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status");
  const surfaceFilter = searchParams.get("surface");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch the full list; status/surface filtering is applied
      // client-side so the column-filter chrome can drive it (the
      // "stuck" pseudo-status has no server equivalent on this endpoint).
      setRows(await api.listSumupTransactions());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const setFilter = (key: "status" | "surface", value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const onForceClose = async (row: SumupTransactionRow) => {
    setClosingId(row.id);
    setError(null);
    setInfo(null);
    try {
      await api.deleteSumupTransaction(row.id);
      setInfo(`Force-closed intent ${row.receipt_no}.`);
      await refresh();
    } catch (err) {
      // The backend returns 409 if a leg turned out to be paid between
      // the render and the click — surface it verbatim.
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setClosingId(null);
    }
  };

  // Distinct surfaces present in the data drive the surface filter options
  // (the policy matrix is extensible, so we don't hardcode the surface set).
  const surfaceOptions = useMemo(() => {
    const seen = new Set(rows.map((r) => r.surface));
    return Array.from(seen)
      .sort()
      .map((s) => ({ value: s, label: s }));
  }, [rows]);

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          matchesStatusFilter(r, statusFilter) &&
          (!surfaceFilter || r.surface === surfaceFilter),
      ),
    [rows, statusFilter, surfaceFilter],
  );

  const stuckCount = useMemo(() => rows.filter((r) => r.stuck).length, [rows]);

  const columns: ColumnsType<SumupTransactionRow> = [
    {
      title: "Receipt #",
      dataIndex: "receipt_no",
      sorter: (a, b) => a.receipt_no.localeCompare(b.receipt_no),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        new Date(a.created_at ?? 0).getTime() -
        new Date(b.created_at ?? 0).getTime(),
      render: (v: string | null) => <TimeCell value={v} />,
    },
    {
      title: "Surface",
      dataIndex: "surface",
      sorter: (a, b) => a.surface.localeCompare(b.surface),
      filteredValue: surfaceFilter ? [surfaceFilter] : null,
      filterDropdown: (props) => (
        <QuickFilterDropdown props={props} options={surfaceOptions} />
      ),
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: "Amount",
      dataIndex: "amount_cents",
      align: "right" as const,
      sorter: (a, b) => a.amount_cents - b.amount_cents,
      render: (v: number) => formatCents(v),
    },
    {
      title: "Direction",
      dataIndex: "direction",
      sorter: (a, b) => a.direction.localeCompare(b.direction),
      render: (v: string) => (
        <Tag>
          {v === "user_to_bank" ? "→ bank" : v === "bank_to_user" ? "← bank" : v}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      // Sort by the derived label so "stuck" rows group together.
      sorter: (a, b) =>
        statusView(a).label.localeCompare(statusView(b).label),
      filteredValue: statusFilter ? [statusFilter] : null,
      filterDropdown: (props) => (
        <QuickFilterDropdown props={props} options={STATUS_FILTER_OPTIONS} />
      ),
      render: (_: unknown, row) => {
        const v = statusView(row);
        return <Tag color={v.color}>{v.label}</Tag>;
      },
    },
    {
      title: "Last error",
      dataIndex: "last_error_code",
      sorter: (a, b) =>
        (a.last_error_code ?? "").localeCompare(b.last_error_code ?? ""),
      render: (v: string | null, row) =>
        v ? (
          <Tooltip title={row.last_error ?? undefined}>
            <Text type="danger" code>
              {v}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Settlement",
      dataIndex: "settlement_id",
      sorter: (a, b) => (a.settlement_id ?? 0) - (b.settlement_id ?? 0),
      render: (v: number | null) =>
        v != null ? (
          <a href={api.settlementReceiptUrl(v)} target="_blank" rel="noreferrer">
            #{v}
          </a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Actions",
      render: (_: unknown, row) => {
        if (row.stuck) {
          // Money was collected — discarding it would lose the payment.
          // Disabled + tooltip; the backend also 409s on this path.
          return (
            <Tooltip title="Card was charged — this payment is being finalized by the poller and must not be discarded. It will settle automatically; reconcile if it persists.">
              <Button danger size="small" disabled>
                Force-close
              </Button>
            </Tooltip>
          );
        }
        // A settled (completed) or already-terminal row has nothing to close.
        if (row.settlement_id != null) return null;
        if (row.status === "cancelled" || row.status === "expired") return null;
        return (
          <Popconfirm
            title="Force-close this intent?"
            description="Discards a dead intent that never collected a payment."
            okText="Force-close"
            okButtonProps={{ danger: true }}
            onConfirm={() => onForceClose(row)}
          >
            <Button danger size="small" loading={closingId === row.id}>
              Force-close
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Card>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
        wrap
      >
        <Title level={2} style={{ margin: 0 }}>
          SumUp transactions
        </Title>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => void refresh()}
        >
          Refresh
        </Button>
      </Space>

      <Paragraph type="secondary">
        Every SumUp payment intent, newest first. A{" "}
        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
          PAYMENT SECURED — SYNCING
        </Tag>{" "}
        row is paid but not yet settled — the poller is finalizing it; the
        money is safe and the row cannot be force-closed.
      </Paragraph>

      {stuckCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${stuckCount} payment${
            stuckCount === 1 ? "" : "s"
          } secured and syncing`}
          description="These cards were charged and are being finalized automatically. No action needed unless they persist — then run a reconcile from the Settlements page."
          action={
            !statusFilter && (
              <Button size="small" onClick={() => setFilter("status", "stuck")}>
                Show only these
              </Button>
            )
          }
        />
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {info && (
        <Alert
          type="success"
          message={info}
          showIcon
          closable
          onClose={() => setInfo(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<SumupTransactionRow>
        columns={columns}
        dataSource={visibleRows}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25 }}
        sticky
        size="middle"
        expandable={{
          expandedRowRender: (row) => <IntentDetail row={row} />,
          rowExpandable: () => true,
        }}
        onChange={(_pag, filters, sorter) => {
          // Column-filter dropdowns mirror their selection into the URL so
          // a deep-link from the stuck badge and the chrome stay in lockstep.
          void (sorter as SorterResult<SumupTransactionRow>);
          const status = filters.status?.[0];
          const surface = filters.surface?.[0];
          setFilter("status", status != null ? String(status) : null);
          setFilter("surface", surface != null ? String(surface) : null);
        }}
      />
    </Card>
  );
};

/** Expanded-row detail: the reassurance line for stuck rows, the intent's
 * error code/message, and the per-channel legs. */
const IntentDetail = ({ row }: { row: SumupTransactionRow }) => (
  <Space direction="vertical" size={12} style={{ width: "100%" }}>
    {row.stuck && (
      <Alert
        type="info"
        showIcon
        message="Card charged — waiting to finalize"
        description="The payment was collected successfully. The background poller is writing the settlement now; the balance will zero out shortly. Nothing was lost."
      />
    )}

    <Descriptions size="small" column={2} bordered>
      <Descriptions.Item label="Intent id">{row.id}</Descriptions.Item>
      <Descriptions.Item label="Updated">
        {formatFullIso(row.updated_at)}
      </Descriptions.Item>
      <Descriptions.Item label="Expires at">
        {formatFullIso(row.expires_at)}
      </Descriptions.Item>
      <Descriptions.Item label="Settlement">
        {row.settlement_id != null ? (
          <a
            href={api.settlementReceiptUrl(row.settlement_id)}
            target="_blank"
            rel="noreferrer"
          >
            #{row.settlement_id}
          </a>
        ) : (
          "—"
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Last error code" span={2}>
        {row.last_error_code ? (
          <Text type="danger" code>
            {row.last_error_code}
          </Text>
        ) : (
          "—"
        )}
      </Descriptions.Item>
      {row.last_error && (
        <Descriptions.Item label="Last error" span={2}>
          <Text type="danger">{row.last_error}</Text>
        </Descriptions.Item>
      )}
    </Descriptions>

    <div>
      <Text strong>Channels</Text>
      {row.legs.length === 0 ? (
        <Paragraph type="secondary" style={{ marginTop: 4 }}>
          No channel armed yet.
        </Paragraph>
      ) : (
        <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 6 }}>
          {row.legs.map((leg: SumupLegRow, idx) => (
            <Space key={`${leg.channel}-${idx}`} size="middle" wrap>
              <Tag>{leg.channel}</Tag>
              <Tag color={LEG_STATUS_COLOR[leg.status] ?? "default"}>
                {leg.status}
              </Tag>
              {leg.last_error_code && (
                <Text type="danger" code>
                  {leg.last_error_code}
                </Text>
              )}
            </Space>
          ))}
        </Space>
      )}
    </div>
  </Space>
);

export default SumupTransactionsPage;
